/**
 * AI 助手悬浮组件
 * ------------------------------------------------------------------
 * 功能：
 *   1. 右下角悬浮 "AI" 按钮（可拖拽）
 *   2. 点击弹出模式选择：页面模式 / 专家模式
 *   3. 页面模式：接入 page-agent 库，管理自身浮动面板，基于页面 DOM 交互
 *   4. 专家模式：全功能 AI 对话（SSE 流式响应、会话管理、思维链展示）
 *
 * 后端接口：
 *   /itoss/v1/chat/completions  — OpenAI 兼容（page-agent 使用）
 *   /itoss/v1/models            — 模型列表（page-agent 使用）
 *   /itoss/opencode/chat         — SSE 流式对话（专家模式 + page-agent 的 ask_opencode 工具）
 *   /itoss/opencode/models       — 获取可用模型
 *   /itoss/opencode/sessions     — 会话列表
 *   /itoss/opencode/session      — 创建会话
 *   /itoss/opencode/session/:id  — 删除会话
 *   /itoss/opencode/session/:id/stop — 中止生成
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Drawer, Button, Input, Select, Tooltip, Tag, Spin, Empty, message } from 'antd';
import {
  CloseOutlined, SendOutlined, ReloadOutlined,
  HistoryOutlined, DeleteOutlined, StopOutlined, BulbOutlined,
  CodeOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { PageAgent, tool } from 'page-agent';
import { z } from 'zod';
import './ai-assistant.less';

// AI 自定义图标（字母A + 闪光）
function AiIcon({ style, className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      style={style}
      className={className}
      fill="currentColor"
    >
      <path d="M16.4 21h-2.154l-2-5H5.754l-2 5H1.6L8 5h2zm4.6-9v9h-2v-9zM6.554 14h4.892L9 7.885zM19.529 2.32a.507.507 0 0 1 .942 0l.253.61a4.37 4.37 0 0 0 2.25 2.327l.717.32a.53.53 0 0 1 0 .962l-.758.338a4.36 4.36 0 0 0-2.22 2.25l-.246.566a.506.506 0 0 1-.934 0l-.247-.565a4.36 4.36 0 0 0-2.219-2.251l-.76-.338a.53.53 0 0 1 0-.963l.718-.32a4.37 4.37 0 0 0 2.251-2.325z" />
    </svg>
  );
}

const { TextArea } = Input;
const { Option } = Select;

// 后端基地址
const API_ORIGIN = `${__API_BASE__ || location.origin}`;
const OPENCODE_BASE = `${API_ORIGIN}/itoss/opencode`;
const LLM_BASE = `${API_ORIGIN}/itoss/v1`;

// ===================== PageAgent 全局状态 =====================
// page-agent 实例和显隐状态存储在 window 上，跨组件/路由共享
function getAgent() { return window.__pageAgent; }
function getShowAgent() { return window.__showAgent; }
function setShowAgent(val) { window.__showAgent = val; }

/**
 * 初始化 PageAgent 实例
 * page-agent 会创建自己的浮动面板 DOM，无需在 React 中渲染
 */
function initAgent() {
  // 销毁旧实例
  if (window.__pageAgent) {
    try { window.__pageAgent.dispose(); } catch { /* ignore */ }
  }

  const agent = new PageAgent({
    model: 'deepseek-v4-flash',
    baseURL: LLM_BASE,
    apiKey: 'page-agent-no-key-needed',
    language: 'zh-CN',
    customTools: {
      ask_opencode: tool({
        description:
          '当用户的问题无法通过页面操作或已有工具回答时，调用此工具获取 ITOSS 智能运维系统的专业知识和业务数据。' +
          '包括：设备监控状态、告警事件、设备台账、报表数据、系统配置等。',
        inputSchema: z.object({
          question: z.string().describe('用户的完整问题，原样传递'),
        }),
        execute: async function (input, { signal }) {
          try {
            const resp = await fetch(`${OPENCODE_BASE}/chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ query: input.question }),
              signal,
            });
            if (!resp.ok) {
              return `[错误] opencode 请求失败 (HTTP ${resp.status})`;
            }

            const reader = resp.body?.getReader();
            if (!reader) return '[错误] 无法读取响应流';

            const decoder = new TextDecoder();
            let buffer = '';
            let result = '';
            let thinking = '';

            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });

              // NDJSON: 按双换行或单换行分割
              const chunks = buffer.split('\n');
              buffer = chunks.pop() || '';

              for (const chunk of chunks) {
                const trimmed = chunk.trim();
                if (!trimmed || !trimmed.startsWith('{')) continue;
                try {
                  const evt = JSON.parse(trimmed);
                  if (evt.type === 'thinking' && evt.result) {
                    thinking += evt.result;
                  } else if (evt.result && !evt.is_end) {
                    result += evt.result;
                  }
                } catch { /* skip */ }
              }
            }

            // 处理剩余缓冲
            if (buffer.trim() && buffer.trim().startsWith('{')) {
              try {
                const evt = JSON.parse(buffer.trim());
                if (evt.result && !evt.is_end) result += evt.result;
              } catch { /* skip */ }
            }

            if (thinking) {
              return `[思考]\n${thinking}\n\n[回答]\n${result || '(无文字回答)'}`;
            }
            return result || '(无回答内容)';
          } catch (err) {
            if (err.name === 'AbortError') return '[已中止]';
            return `[错误] ${err.message}`;
          }
        },
      }),
    },
  });

  window.__pageAgent = agent;
  setShowAgent(true);
  agent.panel.show();

  // 活动事件：自动折叠面板
  agent.addEventListener('activity', () => {
    try { agent.panel.collapse(); } catch { /* ignore */ }
  });

  return agent;
}

/**
 * 切换 PageAgent 面板显隐
 */
function togglePageAgent() {
  const agent = getAgent();
  if (agent) {
    if (agent.disposed) {
      initAgent();
    } else {
      if (getShowAgent()) {
        agent.panel.hide();
      } else {
        agent.panel.show();
      }
      setShowAgent(!getShowAgent());
    }
  } else {
    initAgent();
  }
}

/**
 * 隐藏 PageAgent 面板
 */
function hidePageAgent() {
  const agent = getAgent();
  if (agent) {
    agent.panel.hide();
    setShowAgent(false);
  }
}

// ===================== Markdown 渲染 =====================
function renderMarkdown(text) {
  if (!text) return '';
  let html = text;
  html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => `<pre><code>${code.trim()}</code></pre>`);
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/^### (.+)$/gm, '<p style="font-weight:600;font-size:15px">$1</p>');
  html = html.replace(/^## (.+)$/gm, '<p style="font-weight:600;font-size:16px">$1</p>');
  html = html.replace(/^# (.+)$/gm, '<p style="font-weight:700;font-size:18px">$1</p>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>[\s\S]*?<\/li>)/g, (m) => `<ul>${m}</ul>`);
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

// ===================== 工具调用辅助函数 =====================

/** 序列化工具输入/输出值（截断超长内容） */
function stringifyToolValue(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.slice(0, 12000);
  try {
    return JSON.stringify(value, (_key, item) => {
      if (typeof item === 'string' && item.length > 4000)
        return item.slice(0, 4000) + '\n...(内容过长已截断)';
      return item;
    }, 2).slice(0, 12000);
  } catch {
    return String(value).slice(0, 12000);
  }
}

/** 已知工具名称 → 中文标题映射 */
const TOOL_TITLES = {
  read: '读取文件', write: '写入文件', edit: '编辑文件', bash: '执行命令',
  question: 'AI 提问', todowrite: '更新待办', task: '创建任务',
  skill: '调用技能', webfetch: '网页获取', websearch: '网页搜索',
  ask_opencode: '查询业务数据', glob: '搜索文件', grep: '搜索内容',
  ls: '列出文件', remove: '删除文件', diag: '诊断', process: '进程管理',
};
function toolTitle(name) {
  return TOOL_TITLES[String(name).toLowerCase()] || `调用 ${name}`;
}

/** 状态 → 中文标签 */
function toolStatusText(status) {
  return { pending: '等待中', running: '执行中', completed: '已完成', error: '出错', done: '已完成' }[status] || '已完成';
}

/**
 * 从 SSE part 事件构建工具调用对象
 * part 结构: { id, type:'tool', tool:'工具名', state:{ status, input, output, error, title } }
 */
function buildToolCall(part, existingExpanded) {
  const state = part.state || {};
  const name = String(part.tool || 'tool');
  const input = stringifyToolValue(state.input);
  const output = stringifyToolValue(state.output || state.error);

  const detailParts = [];
  if (input) detailParts.push(`【输入参数】\n${input}`);
  if (output) {
    const label = state.status === 'error' ? '【错误信息】' : '【返回结果】';
    detailParts.push(`${label}\n${output}`);
  }

  const rawStatus = ['pending', 'running', 'completed', 'error'].includes(state.status) ? state.status : 'completed';

  return {
    id: part.id || part.callID || `${name}-${Date.now()}`,
    name,
    title: state.title || toolTitle(name),
    status: rawStatus,
    detail: detailParts.join('\n\n'),
    expanded: existingExpanded ?? false,
  };
}

// ===================== SSE 流式读取（专家模式） =====================
async function streamChat(payload, onEvent, onError) {
  try {
    const resp = await fetch(`${OPENCODE_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try { onEvent(JSON.parse(trimmed)); } catch { /* skip */ }
      }
    }
    if (buffer.trim()) {
      try { onEvent(JSON.parse(buffer.trim())); } catch { /* skip */ }
    }
  } catch (err) {
    onError(err);
  }
}

// ===================== 主组件 =====================
function AiAssistant({ history }) {
  // --- 悬浮按钮 ---
  const [showModePopup, setShowModePopup] = useState(false);
  const [btnPos, setBtnPos] = useState({ right: 32, bottom: 32 });
  const dragState = useRef({ dragging: false, hasMoved: false });

  // --- 专家模式聊天状态 ---
  const [chatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [thinking, setThinking] = useState('');
  const [error, setError] = useState(null);

  // --- 模型 & 会话 ---
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [showSessionList, setShowSessionList] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // --- AI 提问 ---
  const [aiQuestion, setAiQuestion] = useState(null);

  const abortRef = useRef(false);
  const messagesEndRef = useRef(null);

  // ===================== 悬浮按钮拖拽 =====================
  const onDragMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragState.current.hasMoved = true;
    setBtnPos({
      right: Math.max(0, dragState.current.startRight - dx),
      bottom: Math.max(0, dragState.current.startBottom - dy),
    });
  }, []);

  const onDragEnd = useCallback(() => {
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    if (!dragState.current.hasMoved) setShowModePopup(true);
    dragState.current.dragging = false;
  }, []);

  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    dragState.current = {
      dragging: true, hasMoved: false,
      startX: e.clientX, startY: e.clientY,
      startRight: btnPos.right, startBottom: btnPos.bottom,
    };
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
  }, [btnPos]);

  // ===================== 路由变化：控制 PageAgent 面板 =====================
  useEffect(() => {
    if (!history) return;
    const unlisten = history.listen((location) => {
      const agent = getAgent();
      if (!agent) return;
      const path = location.pathname;
      if (path === '/login') {
        agent.panel.hide();
      } else if (getShowAgent()) {
        agent.panel.show();
        try { agent.panel.collapse(); } catch { /* ignore */ }
      } else {
        agent.panel.hide();
      }
    });
    return () => unlisten();
  }, [history]);

  // ===================== 组件卸载：隐藏 PageAgent =====================
  useEffect(() => {
    return () => {
      hidePageAgent();
    };
  }, []);

  // ===================== 获取模型列表 =====================
  useEffect(() => { fetchModels(); }, []);

  async function fetchModels() {
    try {
      const resp = await fetch(`${OPENCODE_BASE}/models`, { credentials: 'include' });
      const data = await resp.json();
      if (data.code === 0 && data.data?.models?.length) {
        setModels(data.data.models);
        setSelectedModel(data.data.defaultModel || data.data.models[0]?.value);
      }
    } catch { /* 静默失败 */ }
  }

  // ===================== 会话管理 =====================
  async function fetchSessions() {
    setLoadingSessions(true);
    try {
      const resp = await fetch(`${OPENCODE_BASE}/sessions`, { credentials: 'include' });
      const data = await resp.json();
      if (data.code === 0) setSessions(data.data || []);
    } catch { /* ignore */ }
    setLoadingSessions(false);
  }

  async function deleteSession(id) {
    try {
      await fetch(`${OPENCODE_BASE}/session/${id}`, { method: 'DELETE', credentials: 'include' });
      message.success('会话已删除');
      fetchSessions();
      if (sessionId === id) { setSessionId(null); setMessages([]); }
    } catch { message.error('删除失败'); }
  }

  // ===================== 模式选择 =====================
  function selectMode(mode) {
    setShowModePopup(false);
    if (mode === 'page') {
      // 页面模式：关闭专家模式抽屉，激活 PageAgent
      setChatVisible(false);
      togglePageAgent();
    } else {
      // 专家模式：隐藏 PageAgent，打开 Drawer
      hidePageAgent();
      openExpertChat();
    }
  }

  function openExpertChat() {
    setChatVisible(true);
    setError(null);
    setMessages([]);
    setSessionId(null);
    setThinking('');
    setAiQuestion(null);
  }

  // ===================== 发送消息（专家模式） =====================
  async function sendMessage(text) {
    const query = (text || inputValue).trim();
    if (!query || streaming) return;

    setInputValue('');
    setError(null);
    setStreaming(true);
    setThinking('');

    const userMsg = { role: 'user', content: query, time: Date.now() };
    const aiMsg = { role: 'assistant', content: '', time: Date.now(), typing: true, tools: [] };
    setMessages(prev => [...prev, userMsg, aiMsg]);
    abortRef.current = false;

    const payload = { query };
    if (selectedModel) payload.model = selectedModel;

    await streamChat(payload, (evt) => {
      if (evt.type === 'session' && evt.sessionId) setSessionId(evt.sessionId);
      if (evt.type === 'thinking' && evt.result) setThinking(prev => prev + evt.result);
      if (evt.result && !evt.type) {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') { last.content += evt.result; last.typing = false; }
          return [...updated];
        });
      }
      if (evt.type === 'part' && evt.part?.type === 'tool') {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          last.tools = last.tools || [];
          const existing = last.tools.find(tc => tc.id === (evt.part.id || evt.part.callID));
          const newTc = buildToolCall(evt.part, existing?.expanded);
          if (existing) {
            Object.assign(existing, newTc);
          } else {
            last.tools.push(newTc);
          }
          return [...updated];
        });
      }
      if (evt.type === 'question' && evt.request) setAiQuestion(evt.request);
      if (evt.type === 'question_resolved') setAiQuestion(null);
      if (evt.is_end) {
        setStreaming(false);
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            last.typing = false;
            if (last.tools?.length) last.tools = last.tools.map(tc => ({ ...tc, status: 'completed' }));
          }
          return [...updated];
        });
        if (evt.code === 1) setError(evt.message || 'AI 服务异常');
      }
    }, (err) => {
      setError(err.message || '连接失败');
      setStreaming(false);
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant') {
          last.typing = false;
          if (!last.content) last.content = '抱歉，连接 AI 服务时出现问题。';
        }
        return [...updated];
      });
    });
  }

  async function stopGeneration() {
    abortRef.current = true;
    if (sessionId) {
      try {
        await fetch(`${OPENCODE_BASE}/session/${sessionId}/stop`, { method: 'POST', credentials: 'include' });
      } catch { /* ignore */ }
    }
    setStreaming(false);
    setMessages(prev => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last && last.role === 'assistant') last.typing = false;
      return [...updated];
    });
  }

  async function replyQuestion(qid, answers) {
    try {
      await fetch(`${OPENCODE_BASE}/question/${qid}/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ answers }),
      });
      setAiQuestion(null);
    } catch { message.error('提交回答失败'); }
  }

  // ===================== 滚动到底部 =====================
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  // ===================== 预设问题（专家模式） =====================
  const suggestions = [
    '查看所有设备的监控状态统计',
    '当前有哪些未处理的告警事件？',
    '帮我查看 CPU 使用率最高的设备 Top10',
    '系统自检结果如何？',
  ];

  // ===================== 渲染 =====================
  return (
    <>
      {/* 悬浮 AI 按钮 */}
      <div
        className="ai-float-btn"
        style={{ right: btnPos.right, bottom: btnPos.bottom }}
        onMouseDown={onDragStart}
        title="AI 助手"
      >
        <AiIcon className="ai-float-btn-icon" />
      </div>

      {/* 模式选择弹窗 */}
      {showModePopup && (
        <div className="ai-mode-overlay" onClick={() => setShowModePopup(false)}>
          <div className="ai-mode-close" onClick={() => setShowModePopup(false)}>
            <CloseOutlined />
          </div>
          <div className="ai-mode-cards" onClick={(e) => e.stopPropagation()}>
            <div className="ai-mode-card" onClick={() => selectMode('page')}>
              <div className="ai-mode-icon ai-mode-icon-page">
                <GlobalOutlined />
              </div>
              <div className="ai-mode-title">页面模式</div>
              <div className="ai-mode-desc">基于当前页面 DOM 智能交互，快速定位和操作页面元素</div>
            </div>
            <div className="ai-mode-card" onClick={() => selectMode('expert')}>
              <div className="ai-mode-icon ai-mode-icon-expert">
                <CodeOutlined />
              </div>
              <div className="ai-mode-title">专家模式</div>
              <div className="ai-mode-desc">全功能 AI 运维助手，支持跨模块查询、数据分析与智能建议</div>
            </div>
          </div>
        </div>
      )}

      {/* 专家模式聊天抽屉 */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AiIcon style={{ color: '#1677ff', width: 18, height: 18 }} />
            <span>AI 运维助手</span>
            <Tag color="purple">专家模式</Tag>
          </div>
        }
        placement="right"
        width={520}
        open={chatVisible}
        onClose={() => {
          if (streaming) stopGeneration();
          setChatVisible(false);
        }}
        className="ai-chat-drawer"
        maskClosable
      >
        {/* 工具栏 */}
        <div className="ai-chat-toolbar">
          <div className="ai-chat-toolbar-left">
            {models.length > 0 && (
              <Select
                size="small"
                value={selectedModel}
                onChange={setSelectedModel}
                style={{ width: 160 }}
                bordered={false}
              >
                {models.map(m => (
                  <Option key={m.value} value={m.value}>{m.label}</Option>
                ))}
              </Select>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip title="会话历史">
              <Button
                size="small"
                type="text"
                icon={<HistoryOutlined />}
                onClick={() => {
                  if (!showSessionList) fetchSessions();
                  setShowSessionList(!showSessionList);
                }}
              />
            </Tooltip>
            <Tooltip title="新对话">
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setMessages([]); setSessionId(null);
                  setThinking('');
                  setError(null); setShowSessionList(false);
                }}
              />
            </Tooltip>
          </div>
        </div>

        {/* 会话历史列表 */}
        {showSessionList && (
          <div className="ai-session-list" style={{ maxHeight: 200, overflowY: 'auto', borderBottom: '1px solid #f0f0f0' }}>
            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
            ) : sessions.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无历史会话" />
            ) : (
              sessions.map(s => (
                <div
                  key={s.id}
                  className={`ai-session-item ${sessionId === s.id ? 'active' : ''}`}
                  onClick={() => { setSessionId(s.id); setShowSessionList(false); }}
                >
                  <div className="ai-session-title">{s.title || s.id}</div>
                  <div className="ai-session-time">
                    {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : ''}
                    <DeleteOutlined
                      style={{ float: 'right', color: '#ff4d4f' }}
                      onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 消息区 */}
        <div className="ai-chat-messages">
          {messages.length === 0 && !streaming ? (
            <div className="ai-chat-welcome">
              <div className="ai-chat-welcome-icon">
                <AiIcon style={{ width: 36, height: 36 }} />
              </div>
              <div className="ai-chat-welcome-title">AI 运维专家</div>
              <div className="ai-chat-welcome-desc">
                我可以帮您查询监控状态、分析告警事件、管理设备资源、生成运维报表等。请告诉我您需要什么帮助？
              </div>
              <div className="ai-chat-suggestions">
                {suggestions.map((s, i) => (
                  <div key={i} className="ai-chat-suggestion" onClick={() => sendMessage(s)}>
                    <BulbOutlined style={{ marginRight: 6, color: '#faad14' }} />
                    {s}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <div key={i} className={`ai-msg ${msg.role === 'user' ? 'ai-msg-user' : ''}`}>
                  <div className={`ai-msg-avatar ${msg.role === 'user' ? 'ai-msg-avatar-user' : 'ai-msg-avatar-ai'}`}>
                    {msg.role === 'user' ? '我' : <AiIcon style={{ width: 16, height: 16 }} />}
                  </div>
                  <div className="ai-msg-content">
                    {msg.role === 'assistant' && thinking && i === messages.length - 1 && streaming && (
                      <div className="ai-msg-thinking">
                        <div className="ai-msg-thinking-label"><BulbOutlined /> 思考中...</div>
                        <div style={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'hidden' }}>
                          {thinking.slice(-500)}
                        </div>
                      </div>
                    )}
                    {msg.role === 'assistant' && msg.tools?.length > 0 && (
                      <div className="ai-tool-calls">
                        {msg.tools.map((tc) => (
                          <div
                            key={tc.id}
                            className={`ai-tool-call ${tc.expanded ? 'is-expanded' : ''}`}
                            onClick={() => {
                              setMessages(prev => {
                                const updated = [...prev];
                                const m = updated[i];
                                if (m?.tools) {
                                  m.tools = m.tools.map(t =>
                                    t.id === tc.id ? { ...t, expanded: !t.expanded } : t
                                  );
                                }
                                return [...updated];
                              });
                            }}
                          >
                            <div className="ai-tool-call-head">
                              <span className="ai-tool-call-icon">⚙</span>
                              <span className="ai-tool-call-title">{tc.title}</span>
                              <span className="ai-tool-call-name">{tc.name}</span>
                              <span className={`ai-tool-call-status is-${tc.status}`}>
                                {tc.status === 'running' && <Spin size="small" style={{ width: 10, height: 10 }} />}
                                {toolStatusText(tc.status)}
                              </span>
                              <span className="ai-tool-call-toggle">
                                {tc.expanded ? '收起' : '详情'}
                              </span>
                            </div>
                            {tc.expanded && tc.detail && (
                              <pre className="ai-tool-call-detail"><code>{tc.detail}</code></pre>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {msg.content ? (
                      <div className="ai-msg-bubble" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                    ) : msg.typing ? (
                      <div className="ai-msg-bubble">
                        <div className="ai-typing-dots"><span></span><span></span><span></span></div>
                      </div>
                    ) : null}
                    {msg.role === 'assistant' && aiQuestion && i === messages.length - 1 && (
                      <div className="ai-question-card">
                        <div className="ai-question-title">{aiQuestion.questions?.[0]?.text || '请选择'}</div>
                        <div className="ai-question-options">
                          {(aiQuestion.questions?.[0]?.options || []).map((opt, k) => (
                            <Button
                              key={k} size="small"
                              onClick={() => replyQuestion(aiQuestion.id, [{ questionId: aiQuestion.questions[0].id, value: opt.value }])}
                            >{opt.label}</Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {error && (
                <div className="ai-msg">
                  <div className="ai-msg-avatar ai-msg-avatar-ai"><AiIcon style={{ width: 16, height: 16 }} /></div>
                  <div className="ai-msg-content"><div className="ai-msg-error">{error}</div></div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 输入区 */}
        <div className="ai-chat-input-area">
          <div className="ai-chat-input-wrapper">
            <TextArea
              className="ai-chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="请输入您的运维问题..."
              autoSize={{ minRows: 1, maxRows: 5 }}
              disabled={streaming}
            />
            {streaming ? (
              <Button className="ai-chat-send-btn" danger icon={<StopOutlined />} onClick={stopGeneration}>停止</Button>
            ) : (
              <Button className="ai-chat-send-btn" type="primary" icon={<SendOutlined />} onClick={() => sendMessage()} disabled={!inputValue.trim()}>发送</Button>
            )}
          </div>
          <div className="ai-chat-input-hint">Enter 发送 · Shift+Enter 换行</div>
        </div>
      </Drawer>
    </>
  );
}

export default withRouter(AiAssistant);
